import React, { useState } from 'react';
import { backdropProps } from '../utils/backdropClose';
import { supabase } from '../supabase';
import { CONSULTANT_CONTACT_ROLES } from '../constants/roles';

// ConsultantContactModal — D4b. Add or edit ONE contact on an existing consultant, with roles.
//   contact = null  -> add mode.   contact = row -> edit mode (prefill + Remove).
// Primary steal: if this contact takes 'primary' and another contact already holds it, that
// other primary role row is deleted (before this contact's roles are inserted), with a heads-up note.
//
// Props: isDark, consultant, contact, existingContacts (consultantContacts array), onClose, onSuccess.
export default function ConsultantContactModal({ isDark, consultant, contact, existingContacts, onClose, onSuccess }) {
  const isEdit = !!contact;
  const [form, setForm] = useState({
    first_name: contact?.first_name || '',
    last_name:  contact?.last_name || '',
    title:      contact?.title || '',
    email:      contact?.email || '',
    phone:      contact?.phone || '',
  });
  const [roles, setRoles] = useState((contact?.consultant_contact_roles || []).map(r => r.role));
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState('');

  const c = isDark
    ? { card:'#2F3854', border:'#374060', text:'#e8eaf2', muted:'var(--slate)', input:'var(--ink)', inputBorder:'#434C68' }
    : { card:'#ffffff', border:'var(--line)', text:'var(--ink)', muted:'var(--slate)', input:'#ffffff', inputBorder:'#c8ccd8' };

  const s = {
    label:    { display:'block', fontSize:'11px', fontWeight:'700', color:c.muted, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'4px' },
    input:    { width:'100%', padding:'8px 11px', background:c.input, border:`1px solid ${c.inputBorder}`, borderRadius:'7px', color:c.text, fontSize:'13px', outline:'none', boxSizing:'border-box', fontFamily:"'Public Sans',system-ui,sans-serif" },
    btn:      { padding:'9px 18px', background:'linear-gradient(135deg,#B40000,#9C0000)', border:'none', borderRadius:'7px', color:'white', fontSize:'13px', fontWeight:'700', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif" },
    btnGhost: { padding:'9px 16px', background:'transparent', border:`1px solid ${c.border}`, borderRadius:'7px', color:c.muted, fontSize:'13px', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif" },
    btnDanger:{ padding:'9px 16px', background:'transparent', border:'1px solid rgba(248,113,113,0.4)', borderRadius:'7px', color:'#f87171', fontSize:'13px', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif" },
    pill: (on) => ({ padding:'6px 14px', borderRadius:'999px', fontSize:'12px', fontWeight:'700', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif",
      border: on ? '1px solid #B40000' : `1px solid ${c.inputBorder}`, background: on ? 'rgba(180,0,0,0.1)' : 'transparent', color: on ? 'var(--red)' : c.muted }),
  };

  function set(field, val) { setForm(prev => ({ ...prev, [field]: val })); }
  function toggleRole(k) { setRoles(prev => prev.includes(k) ? prev.filter(r => r !== k) : [...prev, k]); }

  // Who currently holds primary, other than this contact (the steal target)?
  const primaryHolder = (existingContacts || []).find(x =>
    x.id !== contact?.id && (x.consultant_contact_roles || []).some(r => r.role === 'primary'));
  const willSteal = roles.includes('primary') && !!primaryHolder;

  function validate() {
    if (!form.first_name.trim()) return 'First name is required.';
    if (!form.last_name.trim()) return 'Last name is required.';
    if (roles.length === 0) return 'Select at least one role.';
    return null;
  }

  // Delete any OTHER contact's primary role for this consultant, before inserting this one's roles.
  async function stealPrimaryIfNeeded(thisContactId) {
    if (!roles.includes('primary')) return;
    const { error: delErr } = await supabase.from('consultant_contact_roles')
      .delete().eq('consultant_id', consultant.id).eq('role', 'primary').neq('contact_id', thisContactId);
    if (delErr) throw delErr;
  }

  async function handleSave() {
    const v = validate();
    if (v) { setError(v); return; }
    setSaving(true);
    setError('');
    const fields = {
      first_name: form.first_name.trim(),
      last_name:  form.last_name.trim(),
      title:      form.title.trim() || null,
      email:      form.email.trim() || null,
      phone:      form.phone.trim() || null,
    };
    try {
      if (isEdit) {
        const { error: uErr } = await supabase.from('consultant_contacts').update(fields).eq('id', contact.id);
        if (uErr) throw uErr;
        // Reconcile this contact's roles: delete all -> steal others' primary -> re-insert checked set.
        const { error: dErr } = await supabase.from('consultant_contact_roles').delete().eq('contact_id', contact.id);
        if (dErr) throw dErr;
        await stealPrimaryIfNeeded(contact.id);
        const rows = roles.map(role => ({ contact_id: contact.id, consultant_id: consultant.id, role }));
        const { error: rErr } = await supabase.from('consultant_contact_roles').insert(rows);
        if (rErr) throw rErr;
      } else {
        const { data: created, error: cErr } = await supabase.from('consultant_contacts')
          .insert({ consultant_id: consultant.id, ...fields }).select().single();
        if (cErr) throw cErr;
        try {
          await stealPrimaryIfNeeded(created.id);
          const rows = roles.map(role => ({ contact_id: created.id, consultant_id: consultant.id, role }));
          const { error: rErr } = await supabase.from('consultant_contact_roles').insert(rows);
          if (rErr) throw rErr;
        } catch (inner) {
          // Cleanup: roll back the just-created contact (cascade clears any roles), surface the error.
          await supabase.from('consultant_contacts').delete().eq('id', created.id);
          throw inner;
        }
      }
      onSuccess();
    } catch (e) {
      setError(e?.message || 'Save failed.');
      setSaving(false);
    }
  }

  async function handleRemove() {
    if (!window.confirm(`Remove ${form.first_name} ${form.last_name}? This deletes the contact and its role assignments.`)) return;
    setRemoving(true);
    setError('');
    try {
      const { error: dErr } = await supabase.from('consultant_contacts').delete().eq('id', contact.id);
      if (dErr) throw dErr;
      onSuccess();
    } catch (e) {
      setError(e?.message || 'Remove failed.');
      setRemoving(false);
    }
  }

  const busy = saving || removing;

  return (
    <div {...backdropProps(onClose)}
      style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background:c.card, borderRadius:'14px', padding:'28px', maxWidth:'520px', width:'100%', boxShadow:'0 8px 40px rgba(0,0,0,0.3)', maxHeight:'calc(100vh - 40px)', overflowY:'auto', fontFamily:"'Public Sans',system-ui,sans-serif" }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'4px' }}>
          <div style={{ fontFamily:"'Archivo',sans-serif", fontSize:'20px', fontWeight:'700', color:c.text }}>{isEdit ? 'Edit Contact' : 'Add Contact'}</div>
          <button onClick={onClose} type="button" style={{ background:'none', border:'none', color:c.muted, fontSize:'18px', cursor:'pointer', padding:'0 4px', lineHeight:1 }}>✕</button>
        </div>
        <div style={{ fontSize:'12px', color:c.muted, marginBottom:'22px' }}>{isEdit ? 'Edit details, change roles, or remove this contact.' : `Add a contact for ${consultant.company}.`}</div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'14px' }}>
          <div><label style={s.label}>First Name *</label><input style={s.input} value={form.first_name} onChange={e => set('first_name', e.target.value)} placeholder="First" /></div>
          <div><label style={s.label}>Last Name *</label><input style={s.input} value={form.last_name} onChange={e => set('last_name', e.target.value)} placeholder="Last" /></div>
        </div>
        <div style={{ marginBottom:'14px' }}><label style={s.label}>Title</label><input style={s.input} value={form.title} onChange={e => set('title', e.target.value)} /></div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'14px' }}>
          <div><label style={s.label}>Email</label><input style={s.input} value={form.email} onChange={e => set('email', e.target.value)} /></div>
          <div><label style={s.label}>Phone</label><input style={s.input} value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(000) 000-0000" /></div>
        </div>

        <div style={{ marginBottom:'14px' }}>
          <label style={s.label}>Roles * <span style={{ textTransform:'none', fontWeight:'400', color:c.muted }}>(select one or more)</span></label>
          <div style={{ display:'flex', flexWrap:'wrap', gap:'8px', marginTop:'2px' }}>
            {CONSULTANT_CONTACT_ROLES.map(r => {
              const on = roles.includes(r.key);
              return <button key={r.key} type="button" onClick={() => toggleRole(r.key)} style={s.pill(on)}>{on ? '✓ ' : ''}{r.label}</button>;
            })}
          </div>
          {willSteal && (
            <div style={{ marginTop:'8px', fontSize:'12px', color:'#d97706' }}>
              ⚠ This will replace {primaryHolder.first_name} {primaryHolder.last_name} as Primary.
            </div>
          )}
        </div>

        {error && (
          <div style={{ padding:'10px 12px', marginBottom:'14px', background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:'7px', fontSize:'12px', color:'#f87171' }}>⚠ {error}</div>
        )}

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'22px' }}>
          {isEdit
            ? <button type="button" onClick={handleRemove} disabled={busy} style={s.btnDanger}>{removing ? 'Removing...' : '🗑 Remove contact'}</button>
            : <span />}
          <div style={{ display:'flex', gap:'10px' }}>
            <button type="button" onClick={onClose} disabled={busy} style={s.btnGhost}>Cancel</button>
            <button type="button" onClick={handleSave} disabled={busy} style={{ ...s.btn, opacity: busy ? 0.6 : 1, cursor: busy ? 'default' : 'pointer' }}>
              {saving ? 'Saving...' : (isEdit ? 'Save Changes' : '+ Add Contact')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
